// @ts-nocheck
import dayjs from "dayjs";
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {dropStorage, getStorage, setStorage} from "@/store/util.store";
import {BaseButton, BaseModal} from "../../../../../components";
import useGetUserInfo from "../../../../../hooks/useGetUserInfo";
import useGetUserLastConnection from "../../../../../hooks/useGetUserLastConnection.store";
import usePagoMovilRegistration from "../../../../../hooks/usePagoMovilRegistration";
import useGetUserWallet from "../../../../../hooks/wallet/useGetUserWallet";
import {trackDepositButtonMenuClick} from "../../../../../providers/analytics";
import {useAuthStore} from "../../../../../store/useAuth.store";
import {useBalanceStore} from "../../../../../store/useBalance.store";

const formatDate = (dateString, atTimeLabel) => {
  return dayjs(dateString).format(`DD/MM/YYYY [${atTimeLabel}] HH:mm`);
};

const ModalSessionInfo = () => {
  const [visible, setVisible] = useState(false);
  const authStatus = useAuthStore(state => state.authStatus);
  const {data} = useGetUserInfo();
  const {t} = useTranslation();
  const handleBankAccountModal = useBalanceStore.getState().handleBankAccountModal;
  const {triggerRegistrationModalIfNeeded} = usePagoMovilRegistration();

  const handleDepositAction = async () => {
    trackDepositButtonMenuClick();
    const modalTriggered = await triggerRegistrationModalIfNeeded("deposit", true);
    if (!modalTriggered) {
      handleBankAccountModal();
    }
  };

  useEffect(() => {
    if (authStatus === "authenticated") {
      const hasShown = getStorage("modalSessionInfoShown");
      if (!hasShown) {
        setVisible(true);
        setStorage("modalSessionInfoShown", "true");
      }
    } else {
      setVisible(false);
      dropStorage("modalSessionInfoShown");
    }
  }, [authStatus]);

  const handleClose = () => setVisible(false);

  const {getAccountByCurrency} = useGetUserWallet();
  const vesAccount = getAccountByCurrency("VES");
  const usdAccount = getAccountByCurrency("USD");
  const usdtAccount = getAccountByCurrency("USDT");
  const clpAccount = getAccountByCurrency("CLP");

  const userNameRaw = data?.name || "Nombre usuario";
  const userName = userNameRaw.charAt(0).toUpperCase() + userNameRaw.slice(1);

  const {data: lastConnectionData} = useGetUserLastConnection();
  const atTimeLabel = t("homeModalSessionInfoAtTime", {
    defaultValue: t("homeModalSessionInfoLastConnectionAtTime"),
  });
  const lastConnection = lastConnectionData?.data?.previous_connection
    ? formatDate(lastConnectionData.data.previous_connection, atTimeLabel)
    : "-";

  const activeAccounts = [
    {currency: "VES", label: "Bs", balance: vesAccount?.formattedBalance || "0.00", account: vesAccount},
    {currency: "USD", label: "USD", balance: usdAccount?.formattedBalance || "0.00", account: usdAccount},
    {currency: "USDT", label: "USDT", balance: usdtAccount?.formattedBalance || "0.00", account: usdtAccount},
    {currency: "CLP", label: "CLP", balance: clpAccount?.formattedBalance || "0.00", account: clpAccount},
  ].filter(item => item.account?.balance > 0);

  return (
    <BaseModal
      open={visible}
      onClose={handleClose}
      size="sm"
      closeOnOverlayClick={false}
    >
      {/* Accent bar */}
      <div className="h-[3px] w-full bg-[linear-gradient(90deg,rgba(250,84,0,1)_0%,rgba(215,50,168,1)_100%)] rounded-t-xl" />

      {/* Mini banner placeholder */}
      <div className="
        w-full flex flex-col items-center justify-center gap-2
        bg-[rgba(255,255,255,0.07)] border-b border-[rgba(255,255,255,0.08)]
        aspect-[16/5] min-h-[72px]
      ">
        <div className="w-8 h-8 rounded-full border border-dashed border-[rgba(255,255,255,0.25)] flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
          </svg>
        </div>
        <span className="text-[11px] text-[rgba(255,255,255,0.3)] font-medium tracking-wide select-none">
          Aquí mini banner
        </span>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-4 px-5 pt-5 pb-5">

        {/* Title + last access */}
        <div className="flex flex-col items-center gap-1 text-center">
          <h2 className="m-0 text-xl font-bold text-[var(--color-text)] leading-tight">
            {t("homeModalSessionInfotitle")}
          </h2>
          <p className="m-0 text-[12px] text-[var(--color-text-tertiary)] leading-snug">
            {t("homeModalSessionInfoLastConnection")}:{" "}
            <span className="text-[var(--color-text-secondary)]">{lastConnection}</span>
          </p>
        </div>

        {/* User name */}
        <div className="
          flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl
          bg-[rgba(215,50,168,0.08)] border border-[rgba(215,50,168,0.2)]
        ">
          <div className="
            w-8 h-8 rounded-full shrink-0 flex items-center justify-center
            bg-[linear-gradient(135deg,rgba(250,84,0,0.9)_0%,rgba(215,50,168,0.9)_100%)]
            text-white font-bold text-sm
          ">
            {userName.charAt(0).toUpperCase()}
          </div>
          <span className="font-semibold text-[var(--color-text)] text-sm">{userName}</span>
        </div>

        {/* Balances */}
        {activeAccounts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {activeAccounts.map(item => (
              <div
                key={item.currency}
                className="
                  flex items-baseline gap-1 px-3 py-1.5 rounded-lg
                  bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.08)]
                  text-[12px]
                "
              >
                <span className="text-[var(--color-text-tertiary)]">{item.label}</span>
                <span className="font-semibold text-[var(--color-text)]">{item.balance}</span>
              </div>
            ))}
          </div>
        )}

        {/* CTAs */}
        <div className="flex flex-col gap-2.5 pt-1">
          <button
            type="button"
            onClick={() => { handleDepositAction(); handleClose(); }}
            className="
              w-full h-12 rounded-full font-bold text-sm border-0 cursor-pointer
              bg-[linear-gradient(90deg,rgba(250,84,0,1)_0%,rgba(215,50,168,1)_100%)]
              text-white
              shadow-[0_4px_18px_rgba(215,50,168,0.4),inset_0_1px_0_rgba(255,255,255,0.15)]
              transition-all duration-200 hover:brightness-110 hover:-translate-y-0.5 active:scale-[0.98]
            "
          >
            {t("homeModalSessionInfoGoToDeposit")}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="
              w-full h-11 rounded-full font-medium text-sm cursor-pointer
              bg-transparent border border-[rgba(255,255,255,0.15)]
              text-[var(--color-text-secondary)]
              transition-all duration-200 hover:border-[rgba(255,255,255,0.3)] hover:text-[var(--color-text)]
              active:scale-[0.98]
            "
          >
            {t("homeModalSessionInfoContinue")}
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

ModalSessionInfo.propTypes = {
  user: PropTypes.object,
  onOpenDepositModal: PropTypes.func,
  onClose: PropTypes.func,
};

export default ModalSessionInfo;
